import React from 'react';
import { List } from 'immutable';
import {
    TicketEvent,
    eventIsInternalNoteOrSms,
    eventIsTicketActionEvent,
    eventIsWhatsAppMessage,
    eventIsWhatsAppTemplateMessage,
    ConversationMessageKey,
    eventIsEmail,
    eventIsCallRecordEvent,
    OmniCallEventKey,
    TicketActionEventKey,
} from 'flow/types/TicketEventData';
import { eventIsSupportedForDisplay } from 'flow/utils/ticket-utils';
import { TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import MessageV2 from './event-item/MessageV2';
import MessageExternalServiceV2 from './event-item/MessageExternalServiceV2';
import MessageWhatsAppTemplateV2 from './event-item/MessageWhatsAppTemplateV2';
import EventEmailMessageV2 from './event-item/EventEmailMessageV2';
import EventCallCard from './event-item/call-event/EventCallCard';
import EventTicketAction from './event-item/action-event/EventTicketAction';

interface Props {
    events: List<TypedMap<TicketEvent>>;
    ticketKey: TicketKey;
    onNewAttachmentsFetched?: () => void;
}

const EventsListItemWrapperV2 = ({
    events,
    ticketKey,
    onNewAttachmentsFetched,
}: Props) => {
    return (
        <div className="flex flex-col">
            {events?.toArray().map((eventData) => {
                const type = eventData.get('DTOSubtype');
                const componentKey = `eli-${eventData.get('key')}`;
                const key = eventData.get('key');

                if (eventIsCallRecordEvent(type)) {
                    return (
                        <EventCallCard
                            key={componentKey}
                            eventKey={key as OmniCallEventKey}
                            ticketKey={ticketKey}
                        />
                    );
                }

                if (eventIsEmail(type)) {
                    return (
                        <div key={componentKey} className="py-3">
                            <EventEmailMessageV2
                                ticketKey={ticketKey}
                                messageKey={key as ConversationMessageKey}
                                onNewAttachmentsFetched={
                                    onNewAttachmentsFetched
                                }
                            />
                        </div>
                    );
                }

                if (eventIsInternalNoteOrSms(type)) {
                    return (
                        <div key={componentKey} className="py-3">
                            <MessageV2
                                ticketKey={ticketKey}
                                eventKey={key as ConversationMessageKey}
                                onNewAttachmentsFetched={
                                    onNewAttachmentsFetched
                                }
                            />
                        </div>
                    );
                }

                if (eventIsWhatsAppMessage(type)) {
                    return (
                        <div key={componentKey} className="py-3">
                            <MessageExternalServiceV2
                                messageKey={key as ConversationMessageKey}
                                ticketKey={ticketKey}
                                onNewAttachmentsFetched={
                                    onNewAttachmentsFetched
                                }
                            />
                        </div>
                    );
                }

                if (eventIsWhatsAppTemplateMessage(type)) {
                    return (
                        <div key={componentKey} className="py-3">
                            <MessageWhatsAppTemplateV2
                                ticketKey={ticketKey}
                                messageKey={key as ConversationMessageKey}
                            />
                        </div>
                    );
                }

                if (eventIsTicketActionEvent(type)) {
                    return (
                        <EventTicketAction
                            key={componentKey}
                            ticketKey={ticketKey}
                            eventKey={key as TicketActionEventKey}
                        />
                    );
                }

                // Only unsupported events with content are stored.
                if (!eventIsSupportedForDisplay(type)) {
                    return (
                        <div key={componentKey} className="py-3">
                            <MessageExternalServiceV2
                                messageKey={key as ConversationMessageKey}
                                ticketKey={ticketKey}
                                onNewAttachmentsFetched={
                                    onNewAttachmentsFetched
                                }
                            />
                        </div>
                    );
                }

                // Supported subtype without a branch above: the allow-list in
                // ticket-utils and these branches have drifted.
                return null;
            })}
        </div>
    );
};

export default React.memo(EventsListItemWrapperV2);
